import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
} from "react-bootstrap";
import Stepper from "@/QuestionnaireBuilder/Stepper";
import { useQuestionnaire } from "./QuestionnaireContext";
import { generateClient } from "@aws-amplify/api";
import { getQuestionnaires } from "@/graphql/queries";

const client = generateClient();

export default function QuestionnaireSetup() {
  const navigate = useNavigate();
  const { resetQuestionnaire } = useQuestionnaire();
  const { id } = useParams();

  const [mode, setMode] = useState("new");

  const [selectedSourceId, setSelectedSourceId] = useState("");
  const [existingList, setExistingList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  useEffect(() => {
    resetQuestionnaire();

    fetchExistingQuestionnaires();
  }, []);

  const fetchExistingQuestionnaires = async () => {
    setLoadingList(true);
    try {
      const res = await client.graphql({
        query: getQuestionnaires,
        variables: { limit: 100 },
      });
      const items = res?.data?.getQuestionnaires?.questionnaires || [];
      setExistingList(items);
    } catch (error) {
      console.error("Error fetching questionnaires:", error);
    } finally {
      setLoadingList(false);
    }
  };

  const goNext = () => {
    const path = id
      ? `/app/customers/questionnaires/${id}/questions`
      : `/app/customers/questionnaires/questions`;

    navigate(path, {
      state: {
        setupMode: mode,

        duplicateId: mode === "duplicate" ? selectedSourceId : null,
      },
    });
  };

  const handleCancel = () => {
    navigate('/app/customers/questionnaires');
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          marginTop: "20px",
        }}
      >
        <Stepper labels={["Setup", "Questions", "Review"]} activeIndex={0} />
      </div>

      <Container fluid style={{ maxWidth: "1100px", marginTop: "30px" }}>
        <Row className="justify-content-center">
          <Col xs={12}>
            <Card
              style={{
                border: "3px solid #eaeaea",
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                width: "100%",
                minHeight: "400px",
              }}
            >
              <Card.Body style={{ padding: "2.5rem" }}>
                <h5
                  style={{
                    textAlign: "center",
                    marginBottom: "2rem",
                    fontWeight: 600,
                    color: "#2E2868",
                  }}
                >
                  Create a Questionnaire
                </h5>

                <Form>
                  <div style={{ marginLeft: "120px" }}>
                    {/* Option 1: New questionnaire */}
                    <Form.Check
                      id="opt-new"
                      type="radio"
                      name="mode"
                      style={{ marginBottom: "1.5rem" }}
                      checked={mode === "new"}
                      onChange={() => setMode("new")}
                      label={
                        <div>
                          <div style={{ fontWeight: "500" }}>
                            New questionnaire
                          </div>
                          <div style={{ fontSize: "0.9rem", color: "#6c757d" }}>
                            Create a new questionnaire from scratch.
                          </div>
                        </div>
                      }
                    />

                    {/* Option 2: Duplicate questionnaire */}
                    <Form.Check
                      id="opt-duplicate"
                      type="radio"
                      name="mode"
                      style={{ marginBottom: "0.5rem" }}
                      checked={mode === "duplicate"}
                      onChange={() => setMode("duplicate")}
                      label={
                        <div>
                          <div style={{ fontWeight: "500" }}>
                            Duplicate an existing questionnaire
                          </div>
                          <div style={{ fontSize: "0.9rem", color: "#6c757d" }}>
                            Copy structure from a previous questionnaire.
                          </div>
                        </div>
                      }
                    />

                    <div
                      style={{
                        marginTop: "0.5rem",
                        marginBottom: "2rem",
                        maxWidth: "400px",
                      }}
                    >
                      {loadingList ? (
                        <div
                          style={{
                            fontSize: "0.9rem",
                            color: "#6c757d",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Spinner
                            size="sm"
                            animation="border"
                            variant="primary"
                          />{" "}
                          Loading questionnaires...
                        </div>
                      ) : (
                        <Form.Select
                          disabled={mode !== "duplicate"}
                          value={selectedSourceId}
                          onChange={(e) => setSelectedSourceId(e.target.value)}
                          style={{
                            borderRadius: "50px",
                            padding: "0.6rem 1.2rem",
                            border: "1px solid #ccc",
                            fontSize: "0.95rem",
                            backgroundColor:
                              mode !== "duplicate" ? "#f8f9fa" : "#fff",
                            cursor:
                              mode !== "duplicate" ? "not-allowed" : "pointer",
                          }}
                        >
                          <option value="">-- Select a questionnaire --</option>
                          {existingList.map((q) => (
                            <option key={q.id} value={q.id}>
                              {q.name} (ID: {q.id})
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </div>
                  </div>

                  {/* Buttons */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "2rem",
                    }}
                  >
                    <Button
                      variant="outline-secondary"
                      style={{
                        padding: "0.6rem 2rem",
                        borderRadius: "25px",
                        borderColor: "#2E2868",
                        color: "#6c63ff",
                      }}
                      onClick={handleCancel}
                    >
                      Cancel
                    </Button>

                    <Button
                      style={{
                        padding: "0.6rem 2.5rem",
                        borderRadius: "25px",
                        backgroundColor: "#2E2868",
                        borderColor: "#6c63ff",
                        fontWeight: "500",
                      }}
                      onClick={goNext}
                      disabled={mode === "duplicate" && !selectedSourceId}
                    >
                      Next
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
